"use client";

import {
	createContext,
	type ReactNode,
	type RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useOptimistic,
	useRef,
	useState,
} from "react";
import type { CommentWithDetails, Profile } from "@/types/database";
import { CommentThread } from "./comment-thread";
import { Text } from "./ui/text";

/** Optimistic comment payload: minimal CommentWithDetails for merging into the tree */
interface OptimisticCommentPayload {
	id: number; // Temporary negative numeric ID
	body: string;
	post_id: number | null;
	submission_id: number | null;
	editorial_slug: string | null;
	scene_upload_id?: number | null;
	parent_id: number | null;
	created_at: string;
	updated_at: string;
	is_deleted: false;
	author?: Profile;
	upvotes: number;
	downvotes: number;
	user_vote: null;
	reactions_count: 0;
	reaction_summary: [];
	user_reactions: [];
	replies: CommentWithDetails[];
}

/** Server-returned comment (from createComment action) - may lack author object */
interface ServerCommentRow {
	id: number;
	post_id?: number | null;
	submission_id?: number | null;
	editorial_slug?: string | null;
	parent_id?: number | null;
	body?: string | null;
	created_at: string;
	updated_at: string;
	is_deleted: boolean;
	[key: string]: unknown;
}

interface CommentsComposerAvatar {
	src?: string | null;
	alt: string;
}

interface CommentsContextValue {
	addOptimisticComment: (comment: {
		body: string;
		postId?: number;
		submissionId?: number;
		editorialSlug?: string;
		sceneUploadId?: number;
		parentId?: number | null;
	}) => number;
	/** Add server-confirmed comment so we don't need router.refresh() */
	addConfirmComment: (serverComment: ServerCommentRow) => void;
	/** Optimistic + server thread (for deferred list UI, e.g. submission detail panel tabs). */
	displayComments: CommentWithDetails[];
	threadsContainerRef: RefObject<HTMLDivElement | null>;
	/** Current user for inline reply chrome (avatar). */
	composerAvatar: CommentsComposerAvatar | null;
}

const CommentsContext = createContext<CommentsContextValue | null>(null);

export function useCommentsContext() {
	const context = useContext(CommentsContext);
	if (!context) {
		throw new Error(
			"useCommentsContext must be used within CommentsListClient",
		);
	}
	return context;
}

export function useCommentsContextOptional() {
	return useContext(CommentsContext);
}

function sameScope(
	c: CommentWithDetails,
	postId: number | null | undefined,
	submissionId: number | null | undefined,
	editorialSlug: string | null | undefined,
	sceneUploadId?: number | null,
): boolean {
	if (sceneUploadId != null) {
		return c.scene_upload_id === sceneUploadId;
	}
	if (editorialSlug != null) {
		return c.editorial_slug === editorialSlug;
	}
	if (submissionId != null) {
		return c.submission_id === submissionId;
	}
	if (postId != null) {
		return c.post_id === postId;
	}
	return false;
}

/** True if the tree already has a comment with the same body, parent_id, and post or submission (confirmed copy). */
function treeHasMatchingComment(
	list: CommentWithDetails[],
	body: string,
	parentId: number | null,
	postId: number | null | undefined,
	submissionId: number | null | undefined,
	editorialSlug: string | null | undefined,
	sceneUploadId?: number | null,
): boolean {
	const norm = (s: string | null | undefined) => (s ?? "").trim();
	for (const c of list) {
		if (
			norm(c.body) === norm(body) &&
			(c.parent_id ?? null) === parentId &&
			sameScope(c, postId, submissionId, editorialSlug, sceneUploadId)
		)
			return true;
		if (
			c.replies?.length &&
			treeHasMatchingComment(
				c.replies,
				body,
				parentId,
				postId,
				submissionId,
				editorialSlug,
				sceneUploadId,
			)
		)
			return true;
	}
	return false;
}

/** Newest first: prepend at top-level and prepend within replies. Skips adding if tree already has a matching comment (e.g. confirmed from server) to avoid duplicates. */
function mergeCommentIntoTree(
	comments: CommentWithDetails[],
	newComment: OptimisticCommentPayload,
): CommentWithDetails[] {
	// If we already have a confirmed comment with same body/parent/post, don't add the optimistic one
	if (
		treeHasMatchingComment(
			comments,
			newComment.body,
			newComment.parent_id,
			newComment.post_id,
			newComment.submission_id,
			newComment.editorial_slug,
			newComment.scene_upload_id,
		)
	) {
		return comments;
	}
	const clone = JSON.parse(JSON.stringify(comments)) as CommentWithDetails[];
	const asComment = newComment as unknown as CommentWithDetails;

	if (newComment.parent_id != null) {
		const findAndAdd = (list: CommentWithDetails[]): boolean => {
			for (const c of list) {
				if (c.id === newComment.parent_id) {
					if (!c.replies) c.replies = [];
					c.replies.unshift(asComment);
					return true;
				}
				if (c.replies && findAndAdd(c.replies)) return true;
			}
			return false;
		};
		findAndAdd(clone);
	} else {
		clone.unshift(asComment);
	}
	return clone;
}

interface CommentsListClientProps {
	initialComments: CommentWithDetails[];
	currentUserId?: string | null;
	currentUserProfile?: Profile | null;
	children?: ReactNode;
	/** When set, comments belong to a submission (mirrors post flow for /submissions/[id]). */
	submissionId?: number;
	/** When set, comments belong to a public editorial. */
	editorialSlug?: string;
	/** Hide the default “Please login to comment” footer (e.g. parent already shows sign-in). */
	suppressAnonymousLoginHint?: boolean;
	/**
	 * When false, omit the default count + thread list (render via
	 * `SubmissionCommentsThreadList` inside your layout, e.g. a tab panel).
	 */
	renderDefaultThreadList?: boolean;
}

/** Remove a comment by id from the tree (and its replies). Used to avoid duplicate keys when the same comment exists in initial + confirmed. */
function removeCommentById(
	comments: CommentWithDetails[],
	id: number,
): CommentWithDetails[] {
	return comments
		.filter((c) => c.id !== id)
		.map((c) => ({
			...c,
			replies:
				c.replies && c.replies.length > 0
					? removeCommentById(c.replies, id)
					: c.replies,
		}));
}

/** Build base comment list: initialComments + confirmed (server-returned) comments merged in, newest first. Dedupes by id so the same comment is never in the tree twice. */
function buildBaseComments(
	initialComments: CommentWithDetails[],
	confirmedComments: CommentWithDetails[],
): CommentWithDetails[] {
	let tree = JSON.parse(
		JSON.stringify(initialComments),
	) as CommentWithDetails[];
	for (const c of confirmedComments) {
		tree = removeCommentById(tree, c.id);
		tree = mergeCommentIntoTree(tree, c as unknown as OptimisticCommentPayload);
	}
	return tree;
}

/**
 * Comment count + thread list. Use inside `CommentsListClient` when
 * `renderDefaultThreadList={false}` (e.g. tab panel below a fixed composer).
 */
export function SubmissionCommentsThreadList({
	submissionId: submissionIdProp,
	editorialSlug: editorialSlugProp,
	currentUserId,
}: {
	submissionId?: number;
	editorialSlug?: string;
	currentUserId?: string | null;
}) {
	const { displayComments, threadsContainerRef } = useCommentsContext();
	const showEmptyState = displayComments.length === 0;

	return (
		<>
			{displayComments.length > 0 && (
				<Text className="text-sm text-black/50 dark:text-white/50">
					{displayComments.length}{" "}
					{displayComments.length === 1 ? "comment" : "comments"}
				</Text>
			)}
			<div ref={threadsContainerRef} className="space-y-4 md:pt-0 pb-0">
				{showEmptyState ? (
					<div className="rounded-lg  bg-muted/20 p-6 text-center text-sm text-black/50 dark:text-white/50">
						No comments yet. Be the first to share your thoughts.
					</div>
				) : (
					displayComments.map((comment) => (
						<CommentThread
							key={comment.id}
							comment={comment}
							depth={0}
							postId={comment.post_id ?? undefined}
							submissionId={
								submissionIdProp ?? comment.submission_id ?? undefined
							}
							editorialSlug={
								editorialSlugProp ?? comment.editorial_slug ?? undefined
							}
							currentUserId={currentUserId ?? undefined}
						/>
					))
				)}
			</div>
		</>
	);
}

export function CommentsListClient({
	initialComments,
	currentUserId,
	currentUserProfile,
	children,
	submissionId,
	editorialSlug,
	suppressAnonymousLoginHint = false,
	renderDefaultThreadList = true,
}: CommentsListClientProps) {
	const commentsContainerRef = useRef<HTMLDivElement>(null);
	const lastScrollToIdRef = useRef<number | null>(null);
	const [scrollPending, setScrollPending] = useState(false);
	const [confirmedComments, setConfirmedComments] = useState<
		CommentWithDetails[]
	>([]);
	const previousCommentsCountRef = useRef<number>(initialComments.length);
	const nextTempIdRef = useRef(-1);

	const baseComments = useMemo(
		() => buildBaseComments(initialComments, confirmedComments),
		[initialComments, confirmedComments],
	);

	const [displayComments, addOptimistic] = useOptimistic(
		baseComments,
		(
			state: CommentWithDetails[],
			optimisticComment: OptimisticCommentPayload,
		) => mergeCommentIntoTree(state, optimisticComment),
	);

	const addOptimisticComment = useCallback(
		(comment: {
			body: string;
			postId?: number;
			submissionId?: number;
			editorialSlug?: string;
			sceneUploadId?: number;
			parentId?: number | null;
		}) => {
			if (!currentUserProfile) return 0;

			const postId = comment.postId ?? null;
			const subId = comment.submissionId ?? null;
			const editorialTarget = comment.editorialSlug ?? null;
			const uploadTarget = comment.sceneUploadId ?? null;
			if (
				postId == null &&
				subId == null &&
				editorialTarget == null &&
				uploadTarget == null
			)
				return 0;

			const tempId = nextTempIdRef.current--;
			const now = new Date().toISOString();
			const payload: OptimisticCommentPayload = {
				id: tempId,
				body: comment.body,
				post_id: postId,
				submission_id: subId,
				editorial_slug: editorialTarget,
				scene_upload_id: uploadTarget,
				parent_id: comment.parentId ?? null,
				created_at: now,
				updated_at: now,
				is_deleted: false,
				author: {
					id: currentUserProfile.id,
					username: currentUserProfile.username,
					avatar_url: currentUserProfile.avatar_url,
					role_id: currentUserProfile.role_id,
					onboarding_completed: currentUserProfile.onboarding_completed,
					created_at: currentUserProfile.created_at,
					updated_at: currentUserProfile.updated_at,
					bio: currentUserProfile.bio,
					first_name: currentUserProfile.first_name,
					last_name: currentUserProfile.last_name,
				},
				upvotes: 0,
				downvotes: 0,
				user_vote: null,
				reactions_count: 0,
				reaction_summary: [],
				user_reactions: [],
				replies: [],
			};

			lastScrollToIdRef.current = tempId;
			setScrollPending(true);
			addOptimistic(payload);
			return tempId;
		},
		[currentUserProfile, addOptimistic],
	);

	const addConfirmComment = useCallback(
		(serverComment: ServerCommentRow) => {
			if (!currentUserProfile) return;
			setConfirmedComments((prev) => {
				if (prev.some((c) => c.id === serverComment.id)) return prev;
				const normalized = {
					...serverComment,
					parent_id: serverComment.parent_id ?? undefined,
					author: {
						id: currentUserProfile.id,
						username: currentUserProfile.username,
						avatar_url: currentUserProfile.avatar_url,
						role_id: currentUserProfile.role_id,
						onboarding_completed: currentUserProfile.onboarding_completed,
						created_at: currentUserProfile.created_at,
						updated_at: currentUserProfile.updated_at,
						bio: currentUserProfile.bio,
						first_name: currentUserProfile.first_name,
						last_name: currentUserProfile.last_name,
					},
					upvotes: 0,
					downvotes: 0,
					user_vote: null,
					reactions_count: 0,
					reaction_summary: [],
					user_reactions: [],
					replies: [],
				} as unknown as CommentWithDetails;
				return [...prev, normalized];
			});
		},
		[currentUserProfile],
	);

	const composerAvatar = useMemo((): CommentsComposerAvatar | null => {
		if (!currentUserProfile) return null;
		return {
			src: currentUserProfile.avatar_url,
			alt: currentUserProfile.username,
		};
	}, [currentUserProfile]);

	const contextValue = useMemo(
		() => ({
			addOptimisticComment,
			addConfirmComment,
			displayComments,
			threadsContainerRef: commentsContainerRef,
			composerAvatar,
		}),
		[addOptimisticComment, addConfirmComment, displayComments, composerAvatar],
	);

	// Scroll to newly added optimistic comment when it appears in the tree
	useEffect(() => {
		if (!scrollPending) return;
		const tempId = lastScrollToIdRef.current;
		if (tempId === null) return;

		const timer = setTimeout(() => {
			const el = document.querySelector(
				`[data-comment-id="${tempId}"]`,
			) as HTMLElement | null;
			if (el) {
				el.scrollIntoView({
					behavior: "smooth",
					block: "end",
					inline: "nearest",
				});
			} else {
				const scrollContainer = document.querySelector(
					".card-content",
				) as HTMLElement | null;
				if (scrollContainer) {
					scrollContainer.scrollTo({
						top: scrollContainer.scrollHeight,
						behavior: "smooth",
					});
				}
			}
			lastScrollToIdRef.current = null;
			setScrollPending(false);
		}, 150);

		return () => clearTimeout(timer);
	}, [scrollPending]);

	// Scroll to bottom after server refresh (new comment from server)
	useEffect(() => {
		const currentCount = initialComments.length;
		const previousCount = previousCommentsCountRef.current;
		if (currentCount > previousCount && previousCount > 0) {
			setTimeout(() => {
				const scrollContainer = document.querySelector(
					".card-content",
				) as HTMLElement | null;
				if (scrollContainer) {
					scrollContainer.scrollTo({
						top: scrollContainer.scrollHeight,
						behavior: "smooth",
					});
				}
			}, 300);
		}
		previousCommentsCountRef.current = currentCount;
	}, [initialComments.length]);

	return (
		<CommentsContext.Provider value={contextValue}>
			{children}
			{renderDefaultThreadList ? (
				<SubmissionCommentsThreadList
					submissionId={submissionId}
					editorialSlug={editorialSlug}
					currentUserId={currentUserId}
				/>
			) : null}
			{!currentUserId && !suppressAnonymousLoginHint && (
				<Text className="rounded-lg  bg-muted/20 p-6 text-center text-sm text-black/50 dark:text-white/50">
					Please login to comment.
				</Text>
			)}
		</CommentsContext.Provider>
	);
}
